/**
 * Per-ticker price velocity. Sibling of orderVelocity.ts — same in-memory
 * ring-buffer pattern, different sample shape. Tracks the YES-side mid
 * price for each Kalshi ticker the card grid currently shows, then
 * exposes the price delta over the last 1m + 5m windows so OrderCard and
 * PositionCard can render a "is this market moving against me" chip.
 *
 * YES-native: samples store cents 0–100 from marketStore.getMarketPrice
 * (which already computes YES mid via best-bid+ask average, or falls
 * back to the last trade). Cards on the NO side flip the sign at render
 * time so the on-screen delta reads from the trader's actual side.
 *
 * Polymarket: not wired here today — marketStore doesn't surface poly
 * current price on the position-card VM, so there's no source to sample.
 * Skipping the chip on poly cards is intentional, not a missed wiring.
 */

interface PriceSample {
  t: number;
  /** YES-side mid in cents (0-100). Whole cents — sub-cent moves don't
   *  add information for the trader's 1m read and matching the integer
   *  shape of priceCents elsewhere keeps the chip math readable. */
  priceCents: number;
}

const HISTORY = new Map<string, PriceSample[]>();
const LAST_APPEND_AT = new Map<string, number>();
// 10m max retention — we only need 5m of history for the longest window,
// so a small buffer covers latency + lets the trader open a card after
// being away and see a 5m chip after just a minute of fresh samples.
const MAX_AGE_MS = 10 * 60 * 1000;
// 1s dedup gap — prices move faster than queues, so tighten vs
// orderVelocity's 2s so a fast-moving market still gets enough samples
// for sub-window deltas, without re-rendering the buffer per RAF tick.
const MIN_GAP_MS = 1000;

/** Record the current YES-side mid price for a ticker. Call once per
 *  effect-tick for every visible ticker; MIN_GAP_MS throttling collapses
 *  rapid re-renders into single samples. */
export function recordPriceSample(key: string, priceCents: number): void {
  if (!Number.isFinite(priceCents)) return;
  const rounded = Math.round(priceCents);
  const now = Date.now();
  let arr = HISTORY.get(key);
  if (!arr) {
    arr = [];
    HISTORY.set(key, arr);
  }
  const last = arr[arr.length - 1];
  const lastAppendAt = LAST_APPEND_AT.get(key);
  if (last && lastAppendAt != null && now - lastAppendAt < MIN_GAP_MS) {
    last.t = now;
    last.priceCents = rounded;
  } else {
    arr.push({ t: now, priceCents: rounded });
    LAST_APPEND_AT.set(key, now);
  }
  const cutoff = now - MAX_AGE_MS;
  while (arr.length > 1 && arr[0].t < cutoff) arr.shift();
}

export interface PriceVelocityWindow {
  /** YES-side price change in cents over the window. Positive = YES
   *  price up (which a NO consumer flips to a negative on its side).
   *  Null until history reaches back that far. */
  priceDelta: number | null;
}

const EMPTY: PriceVelocityWindow = { priceDelta: null };

function deltaOver(arr: PriceSample[], windowMs: number): PriceVelocityWindow {
  const cur = arr[arr.length - 1];
  const target = Date.now() - windowMs;
  let base: PriceSample | null = null;
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (arr[i].t <= target) {
      base = arr[i];
      break;
    }
  }
  if (!base) return EMPTY;
  return { priceDelta: cur.priceCents - base.priceCents };
}

export interface PriceVelocity {
  /** 1-minute trailing price delta. The primary read for "is the market
   *  moving against me right now". */
  m1: PriceVelocityWindow;
  /** 5-minute trailing — slower context for whether the 1m move is part
   *  of a sustained trend or just noise. */
  m5: PriceVelocityWindow;
}

/** Price velocity for a ticker across 1m + 5m windows. Returns EMPTY
 *  windows until enough history has accumulated to span them. */
export function priceVelocity(key: string): PriceVelocity {
  const arr = HISTORY.get(key);
  if (!arr || arr.length < 2) return { m1: EMPTY, m5: EMPTY };
  return {
    m1: deltaOver(arr, 60_000),
    m5: deltaOver(arr, 5 * 60_000),
  };
}

/** Helper for cards on the NO side. YES-native delta is `+2¢` when YES
 *  moved 50→52; the NO bid simultaneously moved 50→48, so the trader
 *  reads `-2¢` from their side. Returns null in/out. */
export function sideNativePriceDelta(
  yesDelta: number | null,
  side: 'yes' | 'no',
): number | null {
  if (yesDelta == null) return null;
  return side === 'yes' ? yesDelta : -yesDelta;
}

/** Drop history for tickers no longer rendered. Call from the same
 *  effect that calls recordPriceSample, with the current set of visible
 *  ticker keys. */
export function prunePriceVelocity(activeKeys: Set<string>): void {
  for (const key of HISTORY.keys()) {
    if (!activeKeys.has(key)) {
      HISTORY.delete(key);
      LAST_APPEND_AT.delete(key);
    }
  }
}
